import { DAPI_COOKIE_NAME, getDapiRandomId, setDapiRandomId } from "@2u-landing-pages/utils/dapi-cookie";
import { generateId } from "@2u-landing-pages/utils/generateId";
import { TAGULAR_COOKIE_DOMAIN_JS } from "@2u-landing-pages/utils/tagular-cookie-domain";
import React from "react";

declare global {
  interface Window {
    Cookies: {
      get(name: string): string | undefined;
      set(
        name: string,
        value: string,
        options?: { expires?: number; path?: string; secure?: boolean; sameSite?: "strict" | "lax" }
      ): void;
    };
  }
}

export type CohesionConfig = {
  monarch: {
    sourceId: string;
    clientToken: string;
  };
  make: {
    sourceKey: string;
    writeKey: string;
  };
};

interface CohesionSnippetProps {
  config: CohesionConfig;
  script: string;
  monarchAutoRun?: Boolean;
}

export const CohesionSnippet: React.FC<CohesionSnippetProps> = ({ config, script, monarchAutoRun = true }) => {
  const { monarch, make } = config;

  // Error on build if we do not have the required config for a program.
  if (!monarch?.sourceId || !monarch?.clientToken || !make?.sourceKey || !make?.writeKey) {
    throw new Error(`CohesionSnippet: Missing required configuration values`);
  }

  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `
      window.DAPI_COOKIE_NAME = '${DAPI_COOKIE_NAME}';
      var generateId = ${generateId};
      var getDapiRandomId = ${getDapiRandomId};
      var setDapiRandomId = ${setDapiRandomId};

      // Use the injected functions to get/set DAPI ID
      function getDapiId() {
        var id = getDapiRandomId();
        if (!id) {
          id = generateId();
          setDapiRandomId(id);
        }
        return id;
      }

      !(function (co, h, e, s, i, o, n) {
        var d = "documentElement";
        var a = "className";
        h[d][a] += " preampjs fusejs";
        n.k = e;
        co._Cohesion = n;
        co._Preamp = { k: s, start: new Date() };
        co._Fuse = { k: i };
        co._Tagular = { k: o };
        [e, s, i, o].map(function (x) {
          co[x] =
            co[x] ||
            function () {
              (co[x].q = co[x].q || []).push([].slice.call(arguments));
            };
        });
        var b = function () {
          var u = h[d][a];
          h[d][a] = u.replace(/ ?preampjs| ?fusejs/g, "");
        };
        h.addEventListener("DOMContentLoaded", function () {
          co.setTimeout(b, 3e3);
          co._Preamp.docReady = co._Fuse.docReady = !0;
        });
      })(window, document, "cohesion", "preamp", "fuse", "tagular", {
        tagular: {
          writeKey: "${make.writeKey}",
          sourceKey: "${make.sourceKey}",
          cookieDomain: ${TAGULAR_COOKIE_DOMAIN_JS},
          anonymousCookie: "ajs_anonymous_id",
          pageScrolledDistance: [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100],
        },
        monarch: {
          token: "${monarch.clientToken}",
          sourceId: "${monarch.sourceId}",
          autoRequest: {
            enabled: ${monarchAutoRun},
            hooks: {
              beforeRun: function (config, done) {
                window.__monarchRequestStart = performance.now();
                if (window.newrelic) {
                  window.newrelic.addPageAction('monarch_auto_request', {
                    result: 'success',
                    dapiRandomId: getDapiId()
                  });
                  window.newrelic.interaction().save();
                }
                done();
              },
              afterRun: function (response) {
                if (window.newrelic) {
                  window.newrelic.addPageAction('monarch_auto_request', {
                    result: 'success',
                    dapiRandomId: getDapiId()
                  });
                  window.newrelic.interaction().save();
                }
              }
            },
            params: {
              writeKey: "${make.writeKey}",
            },
            data: {
              application: "rv_creative",
              callType: "autoRequest",
              dapiRandomId: getDapiId(),
            },
          },
          cookies: ["two_u_redirect", "two_u_control", "two_u_creative"],
        },
        consent: {
          onetrust: {
            optIn: true,
            enabled: (() => {
              const params = new URLSearchParams(window.location.search);
              const consentCheck = params.get('consentCheck');
              return consentCheck === null ? true : consentCheck.toLowerCase() === 'true';
            })(),
            timeout: 500,
          },
          required: (() => {
            const params = new URLSearchParams(window.location.search);
            const consentCheck = params.get('consentCheck');
            return consentCheck === null ? true : consentCheck.toLowerCase() === 'true';
          })(),
          cookieClassification: "C0002",
        },
      });
      // Initialize cookie before Monarch loads
      ${script}
    `,
      }}
    ></script>
  );
};
